import React, { useEffect, useState } from 'react'
import { pick } from 'ramda'
import { useSelector } from 'react-redux'

import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridRowMenuItemSpec } from 'core/elements/grid/hooks/useGridRowMenu'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import { HeaderPrimaryActionPortal } from 'core/elements/header/portals'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import usePluginRouter from 'core/hooks/usePluginRouter'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { listConfigMaps } from 'k8s/components/resources/config-maps/actions'
import { configMapsSelector, IConfigMapsTabs } from 'k8s/components/resources/config-maps/selectors'
import DataKeys from 'k8s/DataKeys'
import { durationBetweenDates } from 'utils/misc'
import AddConfigMapModal from './AddConfigMapModal'
import DeleteConfigMapDialog from './DeleteConfigMapDialog'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import useNamespaceFilterDropdown from 'k8s/components/useNamespaceFilterDropdown'
import { isReadonlyUser } from 'core/utils/helpers'

type ModelDataKey = DataKeys.ConfigMaps
type SelectorModel = ArrayElement<ReturnType<typeof configMapsSelector>>
type ActionParams = InferActionParams<typeof listConfigMaps>
//@fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {
  masterNodeClusters: boolean
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId', 'namespace']
const defaultParams: Params & SortingState = {
  clusterId: '',
  namespace: 'default',
  masterNodeClusters: true,
  healthyClusters: true,
  orderBy: 'created',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ConfigMaps',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']
//TODO:: Once BE up test the flow without pluginId
export const getConfigMapColumns = (pluginId = 'kubernetes'): GridViewColumn<SelectorModel>[] => {
  return [
    {
      key: 'name',
      label: 'Name',
      width: 'medium',
      CellComponent: createGridLinkCell({
        routeToFn: ({ clusterName, id }) =>
          routeManager.routes.kubernetes.resources.configMaps.details.path({
            cluster: clusterName,
            id,
            tab: IConfigMapsTabs.Overview,
          }),
      }),
    },
    { key: 'namespace', label: 'Namespace' },
    {
      key: 'created',
      label: 'Age',
      formatFn: (value) => durationBetweenDates({ labels: ['d'] })(value),
    },
    { key: 'dataSize', label: 'Size' },
    {
      key: 'labels',
      label: 'Labels',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
    },
    {
      key: 'annotations',
      label: 'Annotations',
      disableSorting: true,
      CellComponent: createResourceLabelsCell({ type: 'table', separator: ': ' }),
    },
  ]
}

export default function ConfigMapsListPage() {
  const { currentPluginId } = usePluginRouter()
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )
  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const clusterName = k8sPluginGlobalPerfs?.cluster || ''

  const { message, loading, reload } = useListAction(listConfigMaps, {
    params: { clusterId },
    requiredParams,
  })
  const configMaps = useSelector(configMapsSelector)
  const columns = getConfigMapColumns(currentPluginId)

  const [selectedConfigMap, setSelectedConfigMap] = useState<SelectorModel>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)

  const rowMenuItems: Array<GridRowMenuItemSpec<SelectorModel>> = [
    {
      label: 'Delete',
      icon: 'trash-alt',
      handleClick: (configMap) => {
        setSelectedConfigMap(configMap)
        setShowDeleteDialog(true)
      },
      refreshAfterSuccess: true,
    },
  ]
  const { Component, selectedNs } = useNamespaceFilterDropdown()
  const filteredCustomResources =
    configMaps?.filter((item) => selectedNs?.includes(item?.namespace)) || []
  useEffect(() => {
    if (clusterId) {
      reload(true)
    }
  }, [clusterId])
  return (
    <>
      <DocumentMeta title="Config Maps" data-testid="config-maps-document-meta" />
      <AddConfigMapModal data-testid="add-config-map-modal" />
      {showDeleteDialog && (
        <DeleteConfigMapDialog
          onClose={() => setShowDeleteDialog(false)}
          rows={[selectedConfigMap]}
          data-testid="delete-config-map-dialog"
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ConfigMaps}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={filteredCustomResources}
        columns={columns}
        addUrl={routeManager.routes.kubernetes.resources.configMaps.add.path({
          cluster: clusterName,
        })}
        addText={'Add Config Map'}
        getParamsUpdater={getParamsUpdater}
        rowMenuItems={rowMenuItems}
        showItemsCountInLabel
        showRowMenuForSingleRowActions
        extraToolbarContent={Component}
        {...pick(listTablePrefs, params)}
        data-testid="config-maps-list-container"
      />
    </>
  )
}
